import usePlaid from '@/components/plaid/usePlaid';
import { useMutation } from '@apollo/client';
import { MenuItem } from '@chakra-ui/react';
import { ExternalFinancialEntity } from '@common/financialEntities';
import { SetFinancialEntityPermissionDocument } from '@common/graphql/generated';
import { canLink } from '@common/react/financialEntities';
import { isSingleplayer, useCurrentUser } from '@common/react/user';
import ActionMenu from '../common/ActionMenu';
import { Button, MenuButton } from '../common/Button';
import {
    ChevronDown,
    Eye,
    EyeOff,
    Link03,
    Trash01,
    TypeSquare,
} from '../common/icons';
import { showDeleteModal } from './DeleteModal';
import { showRenameModal } from './RenameModal';

export interface FinancialEntityDetailControlsProps {
    fe: ExternalFinancialEntity;
    closeDetailView: () => void;
}

export default function FinancialEntityDetailControls({
    fe,
    closeDetailView,
}: FinancialEntityDetailControlsProps) {
    const { open } = usePlaid({
        financialEntity: {
            id: fe.id,
            type: fe.type,
            linkStatus: fe.linkStatus,
        },
    });
    const currentUser = useCurrentUser();
    const { activePartnershipId } = currentUser;
    const [setFinancialEntityPermission] = useMutation(
        SetFinancialEntityPermissionDocument
    );

    const connectMenuItem = canLink(fe) ? (
        <MenuItem icon={<Link03 />} onClick={open}>
            Connect
        </MenuItem>
    ) : null;

    const renameMenuItem = (
        <MenuItem
            icon={<TypeSquare />}
            onClick={() => {
                showRenameModal({
                    financialEntity: fe,
                });
            }}
        >
            Rename
        </MenuItem>
    );

    const sharingMenuItem =
        // Share status cannot be changed if:
        // 1. User does not have a partnership ID; or
        // 2. User is in singleplayer mode; or
        // 3. Partner is a joint owner of the account
        !activePartnershipId ||
        isSingleplayer(currentUser) ||
        fe.jointOwnerPartnershipId ? (
            <></>
        ) : fe.shareStatus === 'PRIVATE' ? (
            <MenuItem
                icon={<Eye />}
                onClick={() => {
                    // TODO: Consider showing spinner and adding error handling
                    setFinancialEntityPermission({
                        variables: {
                            financialEntityId: fe.id,
                            partnershipId: activePartnershipId,
                            shareStatus: 'SHARED',
                        },
                    });
                }}
            >
                Make shared
            </MenuItem>
        ) : (
            <MenuItem
                icon={<EyeOff />}
                onClick={() => {
                    // TODO: Consider showing spinner and adding error handling
                    setFinancialEntityPermission({
                        variables: {
                            financialEntityId: fe.id,
                            partnershipId: activePartnershipId,
                            shareStatus: 'PRIVATE',
                        },
                    });
                }}
            >
                Make private
            </MenuItem>
        );

    const deleteMenuItem = (
        <MenuItem
            icon={<Trash01 />}
            onClick={() => {
                showDeleteModal({
                    financialEntity: fe,
                    onComplete: closeDetailView,
                });
            }}
        >
            Delete
        </MenuItem>
    );

    return (
        <ActionMenu
            menuButton={
                <MenuButton
                    as={Button}
                    colorScheme="dark"
                    size="sm"
                    w={44}
                    textAlign="left"
                    rightIcon={<ChevronDown size="sm" />}
                    rounded="full"
                    onClickEvent="Financial Entity Detail Controls Menu Button Clicked"
                >
                    Account actions
                </MenuButton>
            }
        >
            {connectMenuItem}
            {renameMenuItem}
            {sharingMenuItem}
            {deleteMenuItem}
        </ActionMenu>
    );
}
